/**
 * useCellQueueJoin — shared query + mutation wiring for the universal queue
 * popup's currently-open cell (CU-86cb7ewja).
 *
 * Shared by the offer-draft availability tab, the inventory catalog, and the
 * media-unit detail calendar — all three render the SAME `InspectPopover`/
 * `QueueJoinPanel` seam, so the query/mutation plumbing behind it lives here
 * once instead of being re-implemented per (already oversized) route file.
 */
import { useCallback, useMemo } from 'react';
import { reservationQueue } from '@ooh/contracts';
import type { QueueEntry } from '@ooh/contracts';
import { useApiQuery } from '../../data/use-api-query';
import { useCurrentUser } from '../../plugs/current-user';
import { queueByUnitRequest } from '../api/queries';
import { useJoinWaitlist, useLeaveWaitlist } from '../api/queue-mutations';
import { mapQueueEntriesToViews } from '../lib/queue-entry-view';
import type { QueueAction, QueueEntryView } from '../components/reservation-queue-list';
import type { QueueJoinPayload } from './queue-join-types';

export interface CellQueueWindowContext {
  mediaUnitId: number;
  dateFrom: string;
  dateTo: string;
  /** False while no cell is open, or the open cell isn't an active hold. */
  enabled: boolean;
  /** Whose active reservation currently holds the open cell. */
  ownership: 'other' | 'mine';
}

export interface ActiveCellQueueState {
  ownership: 'other' | 'mine';
  ownEntries: QueueEntryView[];
  total: number;
}

export interface UseCellQueueJoinResult {
  activeQueueState: ActiveCellQueueState | undefined;
  /** Total-count header data ("N w kolejce") — `undefined` omits the header entirely. */
  queueInfo: { count: number; myPosition: number | null } | undefined;
  currentUserId: number;
  handleJoin: (payload: QueueJoinPayload) => void;
  handleQueueAction: (action: QueueAction, entryId: number) => void;
  isJoinPending: boolean;
  /**
   * True while the underlying queue-by-unit query is actively fetching with no data yet
   * for an `enabled` context (CU-86cbejhae) — always false while `context.enabled` is
   * false (the query never runs). Lets a caller distinguish "not applicable yet" from
   * "genuinely no active hold" for a context that just became enabled, instead of both
   * states looking identical via `activeQueueState === undefined`.
   */
  isQueueLoading: boolean;
}

/**
 * Replaces a prior singular `.find()` "my position" lookup with EVERY one of the
 * caller's own entries — a caller may hold more than one entry on the same window
 * under different client identities. Every active hold gets a state even when the
 * queue is empty: other-held cells need the first-join affordance, while own-held
 * cells need "Dodaj wpis dla innego klienta".
 */
export function resolveActiveQueueState(
  context: CellQueueWindowContext,
  queueData: { items?: QueueEntry[] } | undefined,
  currentUserId: number,
): ActiveCellQueueState | undefined {
  if (!context.enabled || !queueData?.items) return undefined;
  const queue = mapQueueEntriesToViews(queueData.items, context, [context]);
  return {
    ownership: context.ownership,
    ownEntries: queue.filter((view) => view.userId === currentUserId),
    total: queue.length,
  };
}

export function useCellQueueJoin(context: CellQueueWindowContext): UseCellQueueJoinResult {
  const joinWaitlist = useJoinWaitlist();
  const leaveWaitlist = useLeaveWaitlist();
  const currentUserId = useCurrentUser().user?.id ?? 0;

  const { data: queueData, isLoading: isQueueLoading } = useApiQuery(
    reservationQueue.listPosition,
    queueByUnitRequest(context.mediaUnitId),
    { enabled: context.enabled },
  );

  const activeQueueState = useMemo(
    () => resolveActiveQueueState(context, queueData, currentUserId),
    [context, queueData, currentUserId],
  );

  const queueInfo =
    activeQueueState && activeQueueState.total > 0
      ? { count: activeQueueState.total, myPosition: null }
      : undefined;

  const handleJoin = useCallback(
    (payload: QueueJoinPayload) => {
      if (!context.enabled) return;
      joinWaitlist.mutate({
        mediaUnitId: context.mediaUnitId,
        subtype: 'SOFT',
        dateFrom: context.dateFrom,
        dateTo: context.dateTo,
        ...payload,
      });
    },
    [context.enabled, context.mediaUnitId, context.dateFrom, context.dateTo, joinWaitlist],
  );

  const handleQueueAction = useCallback(
    (action: QueueAction, entryId: number) => {
      if (action !== 'leave') return;
      leaveWaitlist.mutate({ id: entryId, mediaUnitId: context.mediaUnitId });
    },
    [context.mediaUnitId, leaveWaitlist],
  );

  return {
    activeQueueState,
    queueInfo,
    currentUserId,
    handleJoin,
    handleQueueAction,
    isJoinPending: joinWaitlist.isPending,
    isQueueLoading,
  };
}
